import React from 'react'

import cx from 'classnames'
import { connect } from 'react-redux'
import { matchPath, useLocation } from 'react-router-dom'

import type { TransactionsSubRouteProps } from '@helpers'
import { getTransactionsSubRoute } from '@helpers'

import { RouteKeys, ROUTES, TmsUserPagePermissions } from '@constants'

import { denyTransactionsPermission, permissionDeniedForUser } from '@permissions'

import { TransactionsPageMessage } from '@messages'
import { LinkSpan, StyledNavLink } from '../styles'

function getNavLinkClassName(displayAsActive?: boolean) {
  return function getter({
    isActive,
    isPending,
    isTransitioning,
  }: Record<'isActive' | 'isPending' | 'isTransitioning', boolean>) {
    return cx({
      active: isActive || displayAsActive,
      pending: isPending,
      transitioning: isTransitioning,
    })
  }
}
function isNavLinkActive(pathname: string) {
  const matchDokuments = matchPath(ROUTES[RouteKeys.DOKUMENT_LIST], pathname)
  const matchDokumentDetails = matchPath(`${ROUTES[RouteKeys.DOKUMENT]}/:document_id`, pathname)
  const matchDokumentsMulti = matchPath(ROUTES[RouteKeys.DOCUMENTS_MULTI_UPLOAD], pathname)
  const matchExpenseCreate = matchPath(ROUTES[RouteKeys.COST], pathname)
  const matchExpenseDetails = matchPath(`${ROUTES[RouteKeys.COST]}/:invoice_id`, pathname)
  const matchExpenseList = matchPath(ROUTES[RouteKeys.EXPENSE_LIST], pathname)
  const matchExpenseMulti = matchPath(ROUTES[RouteKeys.COST_MULTI_UPLOAD], pathname)
  const matchIncomeDetails = matchPath(`${ROUTES[RouteKeys.INCOME]}/:invoice_id?`, pathname) // it handles create as well, do not cross match because there is no "income-multi-upload"
  const matchIncomeList = matchPath(ROUTES[RouteKeys.INCOME_LIST], pathname)
  const matchQuarantineDetails = matchPath(`${ROUTES[RouteKeys.QUARANTINE]}/:invoice_id?`, pathname)
  const matchQuarantineList = matchPath(ROUTES[RouteKeys.QUARANTINE_LIST], pathname)
  const matchTaxList = matchPath(ROUTES[RouteKeys.TAX_LIST], pathname)

  return Boolean(
    matchDokuments ||
      matchDokumentDetails ||
      matchDokumentsMulti ||
      matchExpenseCreate ||
      matchExpenseDetails ||
      matchExpenseList ||
      matchExpenseMulti ||
      matchIncomeDetails ||
      matchIncomeList ||
      matchQuarantineDetails ||
      matchQuarantineList ||
      matchTaxList
  )
}

interface TransactionsNavLinkProps extends TransactionsSubRouteProps {
  blockedByOnboarding: boolean
  hasSubscription: boolean
  lastVisitedSubPage?: string
}

function PureTransactionsNavLink({
  blockedByOnboarding,
  hasSubscription,
  isDokumentsVisibleForUser,
  isExpenseVisibleForUser,
  isIncomeVisibleForUser,
  isQuarantineVisibleForUser,
  isTaxVisibleForUser,
  lastVisitedSubPage,
  tabOrder,
}: TransactionsNavLinkProps) {
  const location = useLocation()

  const isActive = React.useMemo(() => {
    return isNavLinkActive(location.pathname)
  }, [location.pathname])

  const subPageUrl = getTransactionsSubRoute({
    isDokumentsVisibleForUser,
    isExpenseVisibleForUser,
    isIncomeVisibleForUser,
    isQuarantineVisibleForUser,
    isTaxVisibleForUser,
    tabOrder,
  })

  // when no subpage is available
  if (subPageUrl == null) {
    return null
  }

  return (
    <StyledNavLink
      blockedByOnboarding={blockedByOnboarding}
      to={lastVisitedSubPage ?? subPageUrl}
      className={getNavLinkClassName(isActive)}
      noSubscription={!hasSubscription}
      permission={denyTransactionsPermission}
    >
      <LinkSpan>{TransactionsPageMessage}</LinkSpan>
    </StyledNavLink>
  )
}

export const TransactionsNavLink = connect((state: Store) => ({
  isDokumentsVisibleForUser: !permissionDeniedForUser(state, TmsUserPagePermissions.denyDocumentPermission),
  isExpenseVisibleForUser: !permissionDeniedForUser(state, TmsUserPagePermissions.denyExpensePermission),
  isIncomeVisibleForUser: !permissionDeniedForUser(state, TmsUserPagePermissions.denyIncomePermission),
  isQuarantineVisibleForUser: !permissionDeniedForUser(state, TmsUserPagePermissions.denyNavPermission),
  isTaxVisibleForUser: !permissionDeniedForUser(state, TmsUserPagePermissions.denyTaxPermission),
  lastVisitedSubPage: state.controls.lastVisitedSubPage[RouteKeys.TRANSACTIONS],
  tabOrder: state.auth.user.preferences.tab_order,
}))(PureTransactionsNavLink)

TransactionsNavLink.displayName = 'TransactionsNavLink'
